import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { Query } from '@apollo/react-components';
import Helmet from 'react-helmet';
import moment from 'moment';
import qs from 'qs';
import Cookies from 'js-cookie';
import { Toaster } from 'react-hot-toast';
import routes from '@achievable-me/tundra-wizard/routes';
import SignIn from '$components/auth/SignIn';
import SignOut from '$components/auth/SignOut';
import SignUp from '$components/auth/SignUp';
import TalkSso from '$components/auth/TalkSso';
import ForgotPassword from '$components/auth/ForgotPassword';
import ResetPassword from '$components/auth/ResetPassword';
import SpinnerError from '$components/library/SpinnerError';
import {
  AuthenticatedRoute,
  PropsRoute,
  StaffRoute,
  UnauthenticatedRoute,
} from '$components/Routes';
import Catalog from '$components/student/Catalog';
import Tutoring from '$components/student/Tutoring';
import Redeem from '$components/Redeem';
import Resources from '$components/student/Resources';
import Magic from '$components/Magic';
import ProductContent from '$components/ProductContent';
import ProductDashboard from '$components/ProductDashboard';
import ProductOverview from '$components/student/ProductOverview';
import Search from '$components/student/Search';
import EulaBlock from '$components/EulaBlock';
import Essay from '$components/review/Essay';
import Exam from '$components/review/Exam';
import Exams from '$components/Exams';
import Feedback from '$components/Feedback';
import Rewards from '$components/Rewards';
import Subscription from '$components/Subscription';
import Review from '$components/review';
import Share from '$components/review/share.jsx';
import UserAccount from '$components/UserAccount';
import StaffLayout from '$components/layout/StaffLayout';
import Bounce from '$components/Bounce';
import NotFound from '$components/NotFound';
import EnrollWrapper from '$components/EnrollWrapper';
import Welcome from '$components/auth/Welcome';
import Impersonated from '$components/Impersonated';
import StudyReport from '$components/StudyReport';
import CURRENT_USER from '$queries/currentUser';
import { identify } from '$lib/tracking';

import './global.scss';
import styles from './styles.scss';

const ROOT_DOMAIN = process.env.ROOT_DOMAIN; // eslint-disable-line prefer-destructuring
const VERSION = process.env.VERSION; // eslint-disable-line prefer-destructuring

const identifyCallback = (data) => {
  const { currentUser } = data;
  identify(currentUser);
};

const App = () => {
  useEffect(() => {
    const { hash, search } = window.location;

    if (_.isEmpty(search) && _.isEmpty(hash)) {
      return;
    }

    const query = search.substring(1);
    const parsed = qs.parse(query);

    const hashQuery = hash.substring(1);
    const hashParsed = qs.parse(hashQuery);
    const { a_aid: hashSource, p: hashPromo, s: hashSourceShort } = hashParsed;

    const {
      promo,
      p: queryPromoShort,
      s: querySourceShort,
      utm_source: utmSource,
      ref,
    } = parsed;

    const now = +moment();

    const foundSource =
      utmSource || ref || hashSource || hashSourceShort || querySourceShort;
    if (foundSource) {
      Cookies.set('utmSource', foundSource, { domain: ROOT_DOMAIN, expires: 60 });
      Cookies.set('utmSourceAt', now, { domain: ROOT_DOMAIN, expires: 60 });
    }

    const foundPromo = promo || hashPromo || queryPromoShort;
    if (foundPromo) {
      Cookies.set('promo', foundPromo, { domain: ROOT_DOMAIN, expires: 60 });
      Cookies.set('promoAt', now, { domain: ROOT_DOMAIN, expires: 60 });
    }
  }, []);

  return (
    <div className={styles.root} id="app-root">
      <Helmet defaultTitle="Achievable" titleTemplate="%s | Achievable">
        <meta name="achievable-version" content={VERSION} />
      </Helmet>
      <Query query={CURRENT_USER} onCompleted={identifyCallback}>
        {({ loading, error, data }) => {
          if (loading || error) {
            return <SpinnerError error={error} />;
          }

          const { currentUser } = data;

          if (currentUser && !currentUser.termsAt) {
            return <EulaBlock />;
          }

          return (
            <>
              <Impersonated currentUser={currentUser} />
              <Switch>
                <AuthenticatedRoute
                  exact
                  path="/"
                  currentUser={currentUser}
                  component={Bounce}
                />

                <PropsRoute
                  exact
                  path={routes.auth.signIn}
                  component={SignIn}
                  currentUser={currentUser}
                />
                <PropsRoute
                  exact
                  path={routes.auth.signUp}
                  component={SignUp}
                  currentUser={currentUser}
                />

                <UnauthenticatedRoute
                  exact
                  path={routes.auth.forgotPassword}
                  component={ForgotPassword}
                  currentUser={currentUser}
                />
                <UnauthenticatedRoute
                  exact
                  path={routes.auth.resetPassword}
                  component={ResetPassword}
                  currentUser={currentUser}
                />
                <AuthenticatedRoute
                  exact
                  path={routes.auth.signOut}
                  component={SignOut}
                  currentUser={currentUser}
                />

                <AuthenticatedRoute
                  exact
                  path={routes.auth.talkSso}
                  component={TalkSso}
                  currentUser={currentUser}
                />

                <PropsRoute
                  path={routes.enroll.prefix}
                  component={EnrollWrapper}
                  currentUser={currentUser}
                />
                <UnauthenticatedRoute
                  path={routes.auth.welcome}
                  component={Welcome}
                  currentUser={currentUser}
                />

                <AuthenticatedRoute
                  exact
                  path={routes.product.exam}
                  component={Exam}
                  currentUser={currentUser}
                />
                <PropsRoute
                  exact
                  path={routes.product.exams}
                  component={Exams}
                  currentUser={currentUser}
                />
                <AuthenticatedRoute
                  exact
                  path={routes.study.exam}
                  component={Exam}
                  currentUser={currentUser}
                />
                <PropsRoute
                  exact
                  path={routes.study.exams}
                  component={Exams}
                  currentUser={currentUser}
                />
                <AuthenticatedRoute
                  exact
                  path={routes.study.essay}
                  component={Essay}
                  currentUser={currentUser}
                />

                <PropsRoute
                  path={routes.product.learn}
                  component={ProductContent}
                  currentUser={currentUser}
                />
                <PropsRoute
                  path={routes.study.learn}
                  component={ProductContent}
                  currentUser={currentUser}
                />
                <PropsRoute
                  path={routes.study.overview}
                  component={ProductOverview}
                  currentUser={currentUser}
                />
                <PropsRoute
                  path={routes.study.catalog}
                  component={Catalog}
                  currentUser={currentUser}
                />
                <PropsRoute
                  path={routes.study.tutoring}
                  component={Tutoring}
                  currentUser={currentUser}
                />
                <PropsRoute
                  path={routes.study.resources}
                  component={Resources}
                  currentUser={currentUser}
                />
                <PropsRoute
                  path={routes.study.search}
                  component={Search}
                  currentUser={currentUser}
                />

                <AuthenticatedRoute
                  path={routes.product.dashboard}
                  component={ProductDashboard}
                  currentUser={currentUser}
                />
                <PropsRoute
                  path={routes.product.feedback}
                  component={Feedback}
                  currentUser={currentUser}
                />
                <AuthenticatedRoute
                  path={routes.product.review}
                  component={Review}
                  currentUser={currentUser}
                />
                <AuthenticatedRoute
                  path={routes.product.rewards}
                  component={Rewards}
                  currentUser={currentUser}
                />
                <PropsRoute
                  path={routes.product.subscription}
                  component={Subscription}
                  currentUser={currentUser}
                />
                <AuthenticatedRoute
                  path={routes.study.account}
                  component={UserAccount}
                  currentUser={currentUser}
                />
                <AuthenticatedRoute
                  path={routes.study.dashboard}
                  component={ProductDashboard}
                  currentUser={currentUser}
                />
                <PropsRoute
                  path={routes.study.feedback}
                  component={Feedback}
                  currentUser={currentUser}
                />
                <AuthenticatedRoute
                  path={routes.study.review}
                  component={Review}
                  currentUser={currentUser}
                />
                <PropsRoute
                  path={routes.study.quiz}
                  component={Share}
                  currentUser={currentUser}
                />
                <AuthenticatedRoute
                  path={routes.study.rewards}
                  component={Rewards}
                  currentUser={currentUser}
                />
                <PropsRoute
                  path={routes.study.purchase}
                  component={Subscription}
                  currentUser={currentUser}
                />
                <PropsRoute
                  path={routes.study.subscription}
                  component={Subscription}
                  currentUser={currentUser}
                />

                <StaffRoute
                  path={routes.staff.prefix}
                  component={StaffLayout}
                  currentUser={currentUser}
                />

                <PropsRoute
                  exact
                  path="/redeem"
                  component={Redeem}
                  currentUser={currentUser}
                />
                <PropsRoute
                  exact
                  path={routes.magic}
                  component={Magic}
                  currentUser={currentUser}
                />
                <PropsRoute
                  path={routes.studyReport.overview}
                  component={StudyReport}
                  currentUser={currentUser}
                />
                <Route component={NotFound} />
              </Switch>
              {currentUser && (
                <div data-aaa="isAuthenticated" className={styles.hidden} />
              )}
            </>
          );
        }}
      </Query>
      <Toaster position="bottom-center" />
    </div>
  );
};

export default App;
